import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  getArticleBySlug,
  getRelatedArticles,
  getPrevNextArticles,
} from '@/mocks/insightsData';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [showHeaderBg, setShowHeaderBg] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const headerRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);

  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug, 4) : [];
  const prevNext = slug ? getPrevNextArticles(slug) : { prev: null, next: null };

  // 初始化：读取 localStorage
  useEffect(() => {
    if (!slug) return;
    const likedKey = `article_liked_${slug}`;
    const countKey = `article_like_count_${slug}`;
    const bmKey = `article_bookmarks`;
    const likedRaw = localStorage.getItem(likedKey);
    const countRaw = localStorage.getItem(countKey);
    const bmRaw = localStorage.getItem(bmKey);
    setLiked(likedRaw === 'true');
    setLikeCount(countRaw ? parseInt(countRaw, 10) : 128);
    if (bmRaw) {
      const arr = JSON.parse(bmRaw) as string[];
      setBookmarks(arr);
      setBookmarked(arr.includes(slug));
    } else {
      setBookmarks([]);
      setBookmarked(false);
    }
  }, [slug]);

  useEffect(() => {
    if (article) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [article]);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      setShowHeaderBg(scrollY > 120);
      // 阅读进度
      const articleEl = articleRef.current;
      if (articleEl) {
        const rect = articleEl.getBoundingClientRect();
        const articleHeight = articleEl.offsetHeight;
        const windowHeight = window.innerHeight;
        const scrolled = Math.max(0, -rect.top + windowHeight / 2);
        const total = articleHeight + windowHeight / 2;
        const pct = Math.min(100, Math.max(0, (scrolled / total) * 100));
        setReadProgress(pct);
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toast = (msg: string) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleLike = () => {
    if (!slug) return;
    const likedKey = `article_liked_${slug}`;
    const countKey = `article_like_count_${slug}`;
    const newLiked = !liked;
    const newCount = newLiked ? likeCount + 1 : Math.max(0, likeCount - 1);
    setLiked(newLiked);
    setLikeCount(newCount);
    localStorage.setItem(likedKey, String(newLiked));
    localStorage.setItem(countKey, String(newCount));
    toast(newLiked ? '已点赞，感谢支持！' : '已取消点赞');
  };

  const handleBookmark = () => {
    if (!slug) return;
    const bmKey = `article_bookmarks`;
    const newBookmarked = !bookmarked;
    let newBms: string[];
    if (newBookmarked) {
      newBms = [...bookmarks, slug];
    } else {
      newBms = bookmarks.filter((s) => s !== slug);
    }
    setBookmarked(newBookmarked);
    setBookmarks(newBms);
    localStorage.setItem(bmKey, JSON.stringify(newBms));
    toast(newBookmarked ? '已收藏到阅读清单' : '已取消收藏');
  };

  const bookmarkedArticles = bookmarks
    .map((s) => getArticleBySlug(s))
    .filter(Boolean);

  if (!article) {
    return (
      <main className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-surface-warm mx-auto mb-6">
            <i className="ri-file-search-line text-3xl text-text-light" />
          </div>
          <h2 className="text-xl font-bold text-primary mb-2">文章未找到</h2>
          <p className="text-sm text-text-muted mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <a
            href="/insights"
            onClick={(e) => { e.preventDefault(); navigate('/insights'); }}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回资讯中心
          </a>
        </div>
      </main>
    );
  }

  const categoryColors: Record<string, { bg: string; text: string }> = {
    '融慧资讯': { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    '营销数字化': { bg: 'bg-amber-50', text: 'text-amber-600' },
    '白皮书': { bg: 'bg-primary/10', text: 'text-primary-light' },
    '创始人专栏': { bg: 'bg-accent/10', text: 'text-accent' },
  };
  const cc = categoryColors[article.category] || categoryColors['融慧资讯'];

  return (
    <main className="min-h-screen bg-white">
      {/* Toast */}
      <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${showToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        <div className="px-5 py-2.5 bg-foreground-950 text-white text-sm rounded-lg shadow-lg flex items-center gap-2 whitespace-nowrap">
          <i className="ri-check-line text-emerald-400" />
          {toastMsg}
        </div>
      </div>

      {/* Sticky Header */}
      <div
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          showHeaderBg ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100' : ''
        }`}
      >
        {/* Reading Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gray-100">
          <div
            className="h-full bg-accent transition-all duration-150"
            style={{ width: `${readProgress}%` }}
          />
        </div>
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14">
            <a
              href="/insights"
              onClick={(e) => { e.preventDefault(); navigate('/insights'); }}
              className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                showHeaderBg ? 'text-primary hover:text-primary-light' : 'text-white/80 hover:text-white'
              }`}
            >
              <i className="ri-arrow-left-line" />
              资讯中心
            </a>
            {/* Header Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  liked ? 'bg-red-50 text-red-500 border border-red-200' : 'text-text-secondary hover:text-red-500 border border-gray-100 hover:border-red-200'
                }`}
              >
                <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
                <span>{likeCount}</span>
              </button>
              <button
                onClick={handleBookmark}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  bookmarked ? 'bg-amber-50 text-amber-500 border border-amber-200' : 'text-text-secondary hover:text-amber-500 border border-gray-100 hover:border-amber-200'
                }`}
              >
                <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Article Hero */}
      <section className="pt-20 md:pt-28 pb-12 md:pb-20 bg-primary">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl">
            <a
              href="/insights"
              onClick={(e) => { e.preventDefault(); navigate('/insights'); }}
              className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors mb-6 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              资讯中心 / {article.category}
            </a>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10">
                  <i className="ri-user-line text-white/70 text-xs" />
                </div>
                <span>{article.author}</span>
                {article.authorTitle && (
                  <span className="text-white/40">· {article.authorTitle}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-white/40" />
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-white/40" />
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-12 md:py-20 bg-surface-warm">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Main Content */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100" ref={articleRef}>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-surface-warm text-text-secondary text-xs font-medium rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Article Body */}
                  <div className="space-y-0">
                    <p className="text-base md:text-lg text-text-secondary leading-relaxed mb-8 font-medium border-l-4 border-accent pl-4 py-2 bg-accent/10 rounded-r-lg">
                      {article.desc}
                    </p>
                    {article.content.map((paragraph, idx) => {
                      // 图片 URL (远程或本地)
                      if (paragraph.startsWith('http://') || paragraph.startsWith('https://') || paragraph.startsWith('/')) {
                        return (
                          <div key={idx} className="mb-6 rounded-xl overflow-hidden border border-gray-100">
                            <img src={paragraph} alt="" className="w-full object-cover" />
                          </div>
                        );
                      }
                      if (paragraph.startsWith('## ')) {
                        const headingText = paragraph.replace('## ', '');
                        // 检查是否是编号标题 ## 01 / ## #1 / ## 02 / ## 03
                        const numMatch = headingText.match(/^\d+\s/);
                        const hashMatch = headingText.match(/^#\d+\s/);
                        if (numMatch || hashMatch) {
                          const prefix = numMatch ? numMatch[0].trim() : hashMatch![0].trim();
                          const restText = headingText.replace(prefix, '').trim();
                          return (
                            <h2
                              key={idx}
                              className="flex items-center gap-3 text-xl md:text-2xl font-bold text-primary mt-10 mb-4 pt-4 border-t border-gray-100"
                            >
                              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-white text-sm font-bold shrink-0">
                                {prefix}
                              </span>
                              <span>{restText}</span>
                            </h2>
                          );
                        }
                        return (
                          <h2
                            key={idx}
                            className="text-xl md:text-2xl font-bold text-primary mt-10 mb-4 pt-4 border-t border-gray-100"
                          >
                            {headingText}
                          </h2>
                        );
                      }
                      if (paragraph.startsWith('### ')) {
                        return (
                          <h3
                            key={idx}
                            className="text-lg md:text-xl font-bold text-primary mt-8 mb-3"
                          >
                            {paragraph.replace('### ', '')}
                          </h3>
                        );
                      }
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        return (
                          <p key={idx} className="text-sm md:text-base text-primary font-semibold mb-3 mt-6">
                            {paragraph.replace(/\*\*/g, '')}
                          </p>
                        );
                      }
                      if (paragraph.startsWith('> ')) {
                        return (
                          <blockquote
                            key={idx}
                            className="text-sm md:text-base text-text-secondary leading-relaxed mb-4 border-l-4 border-accent pl-4 py-2 bg-surface-warm rounded-r-lg italic"
                          >
                            {paragraph.replace('> ', '')}
                          </blockquote>
                        );
                      }
                      if (paragraph.startsWith('**')) {
                        const text = paragraph.replace(/\*\*/g, '');
                        const colonIdx = text.indexOf('：');
                        if (colonIdx > 0) {
                          return (
                            <div key={idx} className="flex gap-3 mb-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2.5 shrink-0" />
                              <p className="text-sm md:text-base text-text-secondary leading-relaxed">
                                <span className="font-semibold text-primary">{text.slice(0, colonIdx + 1)}</span>
                                {text.slice(colonIdx + 1)}
                              </p>
                            </div>
                          );
                        }
                      }
                      return (
                        <p
                          key={idx}
                          className="text-sm md:text-base text-text-secondary leading-relaxed mb-4"
                        >
                          {paragraph}
                        </p>
                      );
                    })}
                  </div>

                  {/* Share + Like + Bookmark */}
                  <div className="mt-10 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <span className="text-sm text-text-muted">觉得这篇文章有帮助？</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleLike}
                          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                            liked
                              ? 'bg-red-50 text-red-500 border border-red-200'
                              : 'text-text-secondary hover:text-red-500 border border-gray-100 hover:border-red-200'
                          }`}
                        >
                          <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
                          <span>{liked ? '已赞' : '点赞'}</span>
                          <span className="text-xs opacity-70">{likeCount}</span>
                        </button>
                        <button
                          onClick={handleBookmark}
                          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                            bookmarked
                              ? 'bg-amber-50 text-amber-500 border border-amber-200'
                              : 'text-text-secondary hover:text-amber-500 border border-gray-100 hover:border-amber-200'
                          }`}
                        >
                          <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                          <span>{bookmarked ? '已收藏' : '收藏'}</span>
                        </button>
                        <button
                          onClick={() => {
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText(window.location.href);
                              toast('链接已复制到剪贴板');
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm text-text-secondary hover:text-accent border border-gray-100 hover:border-accent/30 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-share-line" />
                          分享
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prev / Next */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prevNext.prev && (
                    <a
                      href={`/insights/article/${prevNext.prev.slug}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/insights/article/${prevNext.prev.slug}`); }}
                      className="text-left p-5 bg-white rounded-xl border border-gray-100 hover:border-accent/30 hover:bg-surface-warm transition-all group cursor-pointer"
                    >
                      <span className="text-xs text-text-muted mb-2 block">上一篇</span>
                      <p className="text-sm font-semibold text-primary group-hover:text-accent transition-colors line-clamp-2">
                        {prevNext.prev.title}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-accent">
                        <i className="ri-arrow-left-line" />
                        阅读更多
                      </div>
                    </a>
                  )}
                  {prevNext.next && (
                    <a
                      href={`/insights/article/${prevNext.next.slug}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/insights/article/${prevNext.next.slug}`); }}
                      className="text-left p-5 bg-white rounded-xl border border-gray-100 hover:border-accent/30 hover:bg-surface-warm transition-all group cursor-pointer"
                    >
                      <span className="text-xs text-text-muted mb-2 block">下一篇</span>
                      <p className="text-sm font-semibold text-primary group-hover:text-accent transition-colors line-clamp-2">
                        {prevNext.next.title}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-accent">
                        阅读更多
                        <i className="ri-arrow-right-line" />
                      </div>
                    </a>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-6">
                  {/* Author Card */}
                  <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
                        <i className="ri-user-line text-primary-light text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-primary">{article.author}</p>
                        {article.authorTitle && (
                          <p className="text-xs text-text-muted">{article.authorTitle}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-text-muted pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" />
                        {article.readTime}
                      </span>
                    </div>
                  </div>

                  {/* Related Articles */}
                  {related.length > 0 && (
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                        <i className="ri-article-line text-accent" />
                        相关文章
                      </h3>
                      <div className="space-y-3">
                        {related.map((ra) => (
                          <a
                            key={ra.slug}
                            href={`/insights/article/${ra.slug}`}
                            onClick={(e) => { e.preventDefault(); navigate(`/insights/article/${ra.slug}`); }}
                            className="w-full text-left group cursor-pointer block"
                          >
                            <p className="text-sm text-text-secondary group-hover:text-accent transition-colors line-clamp-2 mb-1">
                              {ra.title}
                            </p>
                            <span className="text-xs text-text-muted">{ra.date}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bookmarks */}
                  {bookmarkedArticles.length > 0 && (
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                        <i className="ri-bookmark-3-line text-amber-500" />
                        我的收藏
                      </h3>
                      <div className="space-y-3">
                        {bookmarkedArticles.map((ra) => (
                          <a
                            key={ra.slug}
                            href={`/insights/article/${ra.slug}`}
                            onClick={(e) => { e.preventDefault(); navigate(`/insights/article/${ra.slug}`); }}
                            className="w-full text-left group cursor-pointer block"
                          >
                            <p className="text-sm text-text-secondary group-hover:text-accent transition-colors line-clamp-2 mb-1">
                              {ra.title}
                            </p>
                            <span className="text-xs text-text-muted">{ra.date}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Categories */}
                  <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                      <i className="ri-folder-line text-accent" />
                      文章分类
                    </h3>
                    <div className="space-y-2">
                      {['融慧资讯', '营销数字化'].map((cat) => {
                        const counts: Record<string, number> = {
                          '融慧资讯': 6,
                          '营销数字化': 6,
                        };
                        return (
                          <a
                            key={cat}
                            href="/insights"
                            onClick={(e) => { e.preventDefault(); navigate('/insights'); }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                              cat === article.category
                                ? 'bg-accent/10 text-accent font-medium'
                                : 'text-text-secondary hover:bg-surface-warm hover:text-primary'
                            }`}
                          >
                            <span>{cat}</span>
                            <span className="text-xs text-text-muted">{counts[cat]}篇</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-primary rounded-xl p-5 text-white">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <i className="ri-customer-service-2-line" />
                      需要数字化方案咨询？
                    </h3>
                    <p className="text-xs text-white/70 mb-4 leading-relaxed">
                      融慧数科为快消品牌提供一站式营销数字化解决方案。
                    </p>
                    <a
                      href="/contact"
                      onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
                      className="w-full py-2.5 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-accent-dark transition-colors cursor-pointer block text-center whitespace-nowrap"
                    >
                      联系我们
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}